import { type ReactNode, useEffect, useId, useRef } from 'react'
import { BusyButton } from './BusyButton.js'

/**
 * Wat een handeling meeneemt, voordat ze gebeurt: een eindoordeel van de toetser (R-181), het
 * intrekken van een deling (R-66) of het weigeren van een uitnodiging (R-64c).
 *
 * Hetzelfde patroon als `Bevestiging.tsx` in de configurator, dat de renderer niet kan importeren:
 * een `alertdialog` waar de handeling begon, zonder overlay en zonder focusval. De focus gaat
 * naar het paneel zelf en niet naar de knop die doorgaat, want de eerste stop mag niet de
 * onomkeerbare zijn (R-139, WCAG 3.2.1); Escape annuleert.
 *
 * **Tot opdracht 5b stond dit binnen `Toetsknoppen.tsx`.** Intrekken en weigeren vragen dezelfde
 * bevestiging, en een tweede vorm van hetzelfde paneel zou op den duur anders gaan werken. Eén
 * verschil kwam erbij: Escape stopt hier. Staat het paneel in een dialoog (`ShareDialog`), dan
 * sluit Escape alleen de vraag en niet ook de dialoog eromheen.
 */
export interface ConfirmQuestionProps {
  readonly kop: string
  readonly consequence: string
  readonly proceed: string
  readonly soort: 'secundair' | 'gevaar' | 'primair'
  readonly cancel: string
  /** Geeft hij een belofte terug, dan wacht de knop erop met {@link BusyButton}. */
  readonly onProceed: () => unknown
  readonly onCancel: () => void
  /** Wat de doorgaan-knop zegt terwijl hij op de pod wacht ("Bezig met goedkeuren…"). */
  readonly busyText?: string
  /** Bezig van buiten, als de aanroeper zelf bijhoudt dat de pod nog antwoordt. */
  readonly busy?: boolean
}

export function ConfirmQuestion({
  kop: header,
  consequence,
  proceed,
  soort: kind,
  cancel,
  onProceed,
  onCancel,
  busyText,
  busy,
}: ConfirmQuestionProps): ReactNode {
  const panel = useRef<HTMLDivElement>(null)
  const headerId = useId()
  const consequenceId = useId()

  useEffect(() => {
    panel.current?.focus()
  }, [])

  return (
    <div
      ref={panel}
      tabIndex={-1}
      className="ef-bevestigvraag"
      role="alertdialog"
      aria-labelledby={headerId}
      aria-describedby={consequenceId}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        // Niet verder: een dialoog eromheen zou op dezelfde toets ook sluiten.
        event.preventDefault()
        event.stopPropagation()
        onCancel()
      }}
    >
      <p id={headerId} className="ef-bevestigvraag__kop">
        {header}
      </p>
      <p id={consequenceId}>{consequence}</p>
      <div className="ef-bevestigvraag__acties">
        <button type="button" className="ef-actieknop" data-soort="secundair" onClick={onCancel}>
          {cancel}
        </button>
        {busyText === undefined ? (
          <button type="button" className="ef-actieknop" data-soort={kind} onClick={() => void onProceed()}>
            {proceed}
          </button>
        ) : (
          <BusyButton className="ef-actieknop" data-soort={kind} busyText={busyText} onAction={onProceed} {...(busy === undefined ? {} : { busy })}>
            {proceed}
          </BusyButton>
        )}
      </div>
    </div>
  )
}
